import {
  getPurchaseFlowBookingById,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import {
  purchaseFlowBookingIdSelector,
  purchaseFlowBookingSelector,
} from '@/features/purchase/purchaseSelectors';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { intervalToDuration, isAfter } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface CountdownTime {
  minutes: number;
  seconds: number;
}

function getExpiryCountdown(confirmableUntil: string): CountdownTime {
  const now = new Date();
  const end = new Date(confirmableUntil);

  if (isAfter(now, end)) {
    return { minutes: 0, seconds: 0 };
  }

  const duration = intervalToDuration({ start: now, end });

  return {
    minutes: duration.minutes ?? 0,
    seconds: duration.seconds ?? 0,
  };
}

export function useInProgressBooking() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingId = useSelector(purchaseFlowBookingIdSelector);
  const booking = useSelector(purchaseFlowBookingSelector);
  const [isValidating, setIsValidating] = useState(true);
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);

  // Fetch booking if only ID exists (persisted from localStorage)
  useEffect(() => {
    if (bookingId && !booking) {
      dispatch(getPurchaseFlowBookingById(bookingId));
    } else if (booking) {
      // Defensive check: if booking exists from rehydration, validate expiry BEFORE showing modals
      const isExpired = booking.confirmableUntil
        ? isAfter(new Date(), new Date(booking.confirmableUntil))
        : false;

      if (isExpired) {
        // Clean up immediately and keep validating until cleanup completes
        dispatch(resetPurchase());
        persistor.purge().then(() => {
          setIsValidating(false);
        });
      } else {
        setIsValidating(false);
      }
    } else {
      setIsValidating(false);
    }
  }, [bookingId, booking, dispatch]);

  // Auto-cleanup expired bookings
  useEffect(() => {
    if (!booking?.confirmableUntil) return;

    if (isAfter(new Date(), new Date(booking.confirmableUntil))) {
      dispatch(resetPurchase());
      persistor.purge();
    }
  }, [booking?.confirmableUntil, dispatch]);

  // Update countdown every second
  useEffect(() => {
    const confirmableUntil = booking?.confirmableUntil;

    if (!confirmableUntil) {
      setCountdown(null);
      return;
    }

    setCountdown(getExpiryCountdown(confirmableUntil));

    const interval = setInterval(() => {
      setCountdown(getExpiryCountdown(confirmableUntil));
    }, 1000);

    return () => clearInterval(interval);
  }, [booking?.confirmableUntil]);

  const handleCheckoutClick = useCallback(
    (onBeforeNavigate?: () => void) => {
      onBeforeNavigate?.();
      navigate('/purchase/checkout');
    },
    [navigate]
  );

  return {
    bookingId,
    booking,
    isValidating,
    countdown,
    handleCheckoutClick,
  };
}
